import type { ItemId, ScenarioId } from '@finsim/model';
import { useUnit } from 'effector-react';
import { Copy, CopyPlus, Eye, EyeOff, Focus, Link2, Minus, Split, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  $visibility,
  muteToggled,
  soloToggled,
  visibilityKey,
} from '../../entities/item-visibility';
import { $project, $readOnly, usageCount } from '../../entities/project';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '../../shared/ui';
import {
  copyToRequested,
  deleteEverywhereRequested,
  detachRequested,
  duplicateRequested,
  removeRequested,
  shareRequested,
} from './model';

/** Context-menu items for an item row. */
export function ItemContextMenuItems({
  itemId,
  scenarioId,
}: {
  itemId: ItemId;
  scenarioId: ScenarioId;
}) {
  const { t } = useTranslation();
  const [visibility, toggleMute, toggleSolo, copyTo] = useUnit([
    $visibility,
    muteToggled,
    soloToggled,
    copyToRequested,
  ]);
  const [project, readOnly, duplicate, share, detach, remove, deleteEverywhere] = useUnit([
    $project,
    $readOnly,
    duplicateRequested,
    shareRequested,
    detachRequested,
    removeRequested,
    deleteEverywhereRequested,
  ]);
  const item = project?.items[itemId];
  if (!project || !item) return null;
  const uses = usageCount(project).get(itemId) ?? 0;
  const others = project.scenarios.filter(
    (s) => s.id !== scenarioId && !s.itemIds.includes(itemId),
  );
  const copyTargets = project.scenarios.filter((s) => s.id !== scenarioId);
  const muted = visibility.muted.has(visibilityKey(scenarioId, itemId));
  const soloed = visibility.soloed.get(scenarioId) === itemId;
  const dot = (color: number) => (
    <span
      className="size-2 rounded-full"
      style={{ background: `var(--chart-series-${String(color + 1)})` }}
    />
  );

  const visibilityItems = (
    <>
      <ContextMenuItem
        icon={muted ? <Eye /> : <EyeOff />}
        shortcut={['M']}
        onSelect={() => {
          toggleMute({ itemId, scenarioId });
        }}
      >
        {muted ? t('visibility.unmute') : t('visibility.mute')}
      </ContextMenuItem>
      <ContextMenuItem
        icon={<Focus />}
        shortcut={['S']}
        onSelect={() => {
          toggleSolo({ itemId, scenarioId });
        }}
      >
        {soloed ? t('visibility.unsolo') : t('visibility.solo')}
      </ContextMenuItem>
    </>
  );
  // A shared project: only mute and solo, which change what's shown, not the project.
  if (readOnly) return visibilityItems;

  return (
    <>
      <ContextMenuItem
        icon={<Copy />}
        shortcut={['⌘', 'D']}
        onSelect={() => {
          duplicate({ itemId, scenarioId, name: t('itemActions.copyName', { name: item.name }) });
        }}
      >
        {t('itemActions.duplicate')}
      </ContextMenuItem>
      {others.length > 0 && (
        <ContextMenuSub>
          <ContextMenuSubTrigger icon={<Link2 />}>
            {t('itemActions.shareWith')}
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {others.map((s) => (
              <ContextMenuItem
                key={s.id}
                icon={
                  <span
                    className="size-2 rounded-full"
                    style={{ background: `var(--chart-series-${String(s.color + 1)})` }}
                  />
                }
                onSelect={() => {
                  share({ itemId, scenarioId: s.id });
                }}
              >
                {s.name}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      {copyTargets.length > 0 && (
        <ContextMenuSub>
          <ContextMenuSubTrigger icon={<CopyPlus />}>
            {t('itemActions.copyTo')}
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {copyTargets.map((s) => (
              <ContextMenuItem
                key={s.id}
                icon={dot(s.color)}
                onSelect={() => {
                  copyTo({ itemId, scenarioId: s.id });
                }}
              >
                {s.name}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      {uses > 1 && (
        <ContextMenuItem
          icon={<Split />}
          onSelect={() => {
            detach({ itemId, scenarioId });
          }}
        >
          {t('itemActions.detach')}
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
      {visibilityItems}
      <ContextMenuSeparator />
      {uses > 1 && (
        <ContextMenuItem
          icon={<Minus />}
          shortcut={['⌫']}
          onSelect={() => {
            remove({ itemId, scenarioId });
          }}
        >
          {t('itemActions.removeFromVariant')}
        </ContextMenuItem>
      )}
      <ContextMenuItem
        danger
        icon={<Trash2 />}
        {...(uses > 1 ? {} : { shortcut: ['⌫'] })}
        onSelect={() => {
          if (uses > 1) deleteEverywhere({ itemId });
          else remove({ itemId, scenarioId });
        }}
      >
        {uses > 1 ? t('itemActions.deleteEverywhere') : t('itemActions.delete')}
      </ContextMenuItem>
    </>
  );
}
